<!-- Copyright 2018-2019 Unryo. All Rights Reserved. -->

<template>
  <v-layout column>
    <v-alert :value="missing" type="warning" outlined dismissible>
      {{ $t('missing') }}
    </v-alert>
    <v-alert :value="error" type="error" outlined>
      {{ $t('error') }}
    </v-alert>
    <v-card>
      <v-spacer></v-spacer>
      <div class="map" ref="map"></div>
      <div ref="popup" class="ol-popup">
        <div class="ol-popup-closer">
          <v-btn text icon @click="closePopup()">
            <v-icon>close</v-icon>
          </v-btn>
        </div>
        <app-node-properties v-if="popupItems" :data="popupItems"></app-node-properties>
      </div>
      <div class="pending" v-if="pending">
        <v-progress-circular
          :size="70"
          :width="7"
          color="primary"
          indeterminate
        >
        </v-progress-circular>
      </div>
    </v-card>
  </v-layout>
</template>

<script>
import TooltipComponent from '@/components/Tooltip.vue'
import GeoLocationService from '@/services/geolocation'
import { initMap } from '@/services/geomap'
import NetworkService, { nodeIdToDisplayName } from '@/services/network'
import { delayUntilVisible } from '@/services/utils'

export default {
  data () {
    return {
      /**
       * Raised when a fatal error is encountered.
       */
      error: false,
      /**
       * Map actions reference, used to trigger actions on the geomap.
       */
      map: null,
      /**
       * Raised when some nodes location is missing.
       */
      missing: false,
      /**
       * Whether items are still being retrieved or not.
       */
      pending: true,
      /**
       * Data for items to be displayed in the popup.
       */
      popupItems: null
    }
  },
  mounted () {
    this.pending = true

    // geomap can only be initialized once the display animation is complete
    Promise.all([
      NetworkService.nodes().then((nodes) => this.enrichNodes(nodes)).catch(() => []),
      delayUntilVisible(this.$refs, 'map')
    ]).then(([ markers ]) => {
      if (!markers.length) {
        this.error = true
        return
      }
      this.map = initMap(
        this.$refs.map,
        markers,
        this.$refs.popup,
        {
          onPopupOpened: (items) => this.onPopupOpened(items)
        }
      )
    }).finally(() => {
      this.pending = false
    })

    this.$root.$on('wizardClose', () => {
      this.reset()
    })
  },
  methods: {
    /**
     * Resets the geomap markers.
     */
    reset () {
      this.pending = true
      NetworkService.nodes().then((nodes) => this.enrichNodes(nodes)).catch(() => []).then((markers) => {
        if (this.map) {
          if (!markers.length) {
            this.error = true
            return
          }
          this.map.reset(markers)
        }
      }).finally(() => {
        this.pending = false
      })
    },
    /**
     * Adds geolocation information to the provided nodes.
     *
     * @param nodes Nodes wich ip address information.
     * @returns A Promise that resolves in an array of enriched nodes.
     */
    enrichNodes (nodes) {
      // extract unique IPs from nodes
      const ips = [...new Set(nodes.map((node) => node.metadata.externalIp))].filter((ip) => !!ip)

      // retrieve locations from those IPs
      const entries = ips.map((ip) => Promise.all([ip, GeoLocationService.getGeoLocationFromIp(ip).catch(() => null)]))

      // apply the retrieved locations to the provided nodes
      return Promise.all(entries).then((res) => {
        const data = []

        // map location by IP for quick lookup
        const locations = {}
        for (const [ip, location] of res) {
          locations[ip] = location
        }

        // assign location to nodes
        for (const node of nodes) {
          const location = locations[node.metadata.externalIp]
          if (!location) {
            this.missing = true
          } else {
            // the following content will be used within each popup
            data.push(this.extractGeolocationData(node, location))
          }
        }

        return data
      })
    },
    /**
     * Extracts geolocation data from the node and its location.
     *
     * @param node Complete data.
     * @param location Node location
     * @returns Useful node data and location for the geomap.
     */
    extractGeolocationData (node, location) {
      return {
        id: node.id,
        label: nodeIdToDisplayName(node.label),
        group: node.group || 'unknown',
        namespace: node.namespace,
        roles: node.roles,
        timestamp: node.timestamp,
        metadata: {
          externalIp: node.metadata.externalIp
        },
        health: {
          status: node.health.status,
          availabilityIssue: node.health.availabilityIssue,
          performanceIssue: node.health.performanceIssue
        },
        lat: location.latitude,
        lon: location.longitude,
        provider: location.provider
      }
    },
    /**
     * Geomap handles when to open the popup; we just react to it.
     *
     * @param item The items for which the popup was opened.
     */
    onPopupOpened (items) {
      this.popupItems = items
    },
    /**
     * Closing the popup can be handled from this component through the corresponding button.
     *
     * This lets the map know a close order was issued.
     */
    closePopup () {
      if (this.map) {
        this.map.closePopup()
      }
    }
  },
  components: {
    'app-node-properties': TooltipComponent
  }
}
</script>

<style lang="scss">
.v-card, .map {
  height: 100%;
}

.v-alert {
  left: 50%;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  z-index: 1;
}

.pending {
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  text-align: center;
  top: 0;

  > .v-progress-circular {
    top: calc(50% - 35px);
  }
}

// hides the popup while the map initializes
.map ~ .ol-popup {
  display: none;
}

.ol-popup {
  background-color: #fff;
  border: 1px solid #ccc;
  bottom: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  left: -50px;
  min-width: 350px;
  padding-right: 2rem;
  position: absolute;
  transform: translate(1px, -35px);
}

.ol-popup:after, .ol-popup:before {
  border: solid transparent;
  content: " ";
  height: 0;
  pointer-events: none;
  position: absolute;
  top: 100%;
  width: 0;
}

.ol-popup:after {
  border-top-color: #fff;
  border-width: 10px;
  left: 48px;
  margin-left: -10px;
}

.ol-popup:before {
  border-top-color: #ccc;
  border-width: 11px;
  left: 48px;
  margin-left: -11px;
}

.ol-popup-closer {
  position: absolute;
  right: 0;
  top: 0;
}
</style>

<i18n>
{
  "en": {
    "title": "GeoMap",
    "missing": "Some nodes could not be positioned and will not be displayed on the map.",
    "error": "We couldn't find any nodes to display on the map..."
  }
}
</i18n>